import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "contexts/AuthContext";
import {
  Schedule,
  LocationOn,
  Info,
  Refresh,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonButton from 'components/ArgonButton';

// Common US timezones for the dropdown
const DEFAULT_TIMEZONES = {
  "America/New_York": "Eastern Time (US & Canada)",
  "America/Chicago": "Central Time (US & Canada)",
  "America/Denver": "Mountain Time (US & Canada)",
  "America/Los_Angeles": "Pacific Time (US & Canada)",
  "America/Anchorage": "Alaska",
  "Pacific/Honolulu": "Hawaii",
  "America/Phoenix": "Arizona (no DST)",
  "Asia/Jerusalem": "Jerusalem",
  "Europe/London": "London",
  "Europe/Paris": "Paris / Central Europe",
};

const SabbathSettings = ({
  onSettingsUpdate,
  showStatus = true
}) => {
  const { user } = useAuth();

  const [settings, setSettings] = useState({
    observe_sabbath: false,
    company_timezone: 'America/New_York',
    company_city: '',
    company_latitude: null,
    company_longitude: null,
    sabbath_start_buffer_minutes: 18,
    sabbath_end_buffer_minutes: 42
  });

  const [sabbathStatus, setSabbathStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    loadSettingsFromFirestore();
  }, [user]);

  useEffect(() => {
    if (settings.observe_sabbath && showStatus) {
      computeSabbathStatus();
    }
  }, [settings.company_timezone, settings.company_city, settings.observe_sabbath]);

  const loadSettingsFromFirestore = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const sabbathSettings = data.sabbathSettings || {};
        setSettings({
          observe_sabbath: sabbathSettings.observe_sabbath || false,
          company_timezone: sabbathSettings.company_timezone || 'America/New_York',
          company_city: sabbathSettings.company_city || '',
          company_latitude: sabbathSettings.company_latitude || null,
          company_longitude: sabbathSettings.company_longitude || null,
          sabbath_start_buffer_minutes: sabbathSettings.sabbath_start_buffer_minutes ?? 18,
          sabbath_end_buffer_minutes: sabbathSettings.sabbath_end_buffer_minutes ?? 42,
        });
      }
    } catch (err) {
      console.error('Error loading sabbath settings:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  // Compute sabbath status client-side based on day/time
  const computeSabbathStatus = () => {
    setStatusLoading(true);
    try {
      const now = new Date();
      // Use Intl to get current time in user's timezone
      const tz = settings.company_timezone || 'America/New_York';
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const parts = formatter.formatToParts(now);
      const weekday = parts.find(p => p.type === 'weekday')?.value;
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);

      // Approximate: Sabbath is Friday ~18:00 to Saturday ~20:00 (adjusted by buffers)
      const startHour = 18 - (settings.sabbath_start_buffer_minutes || 18) / 60;
      const endHour = 20 + (settings.sabbath_end_buffer_minutes || 42) / 60;

      let isSabbath = false;
      if (weekday === 'Friday' && hour >= startHour) isSabbath = true;
      if (weekday === 'Saturday' && hour < endHour) isSabbath = true;

      // Approximate sabbath times for this week
      const today = new Date(now);
      const dayOfWeek = today.getDay(); // 0=Sun
      const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
      const nextFriday = new Date(today);
      nextFriday.setDate(today.getDate() + (daysUntilFriday === 0 && hour >= startHour ? 7 : daysUntilFriday));
      nextFriday.setHours(Math.floor(startHour), (startHour % 1) * 60, 0, 0);

      const nextSaturday = new Date(nextFriday);
      nextSaturday.setDate(nextFriday.getDate() + 1);
      nextSaturday.setHours(Math.floor(endHour), (endHour % 1) * 60, 0, 0);

      setSabbathStatus({
        is_sabbath: isSabbath,
        observes_sabbath: settings.observe_sabbath,
        sabbath_times: {
          sabbath_start: nextFriday.toISOString(),
          sabbath_end: nextSaturday.toISOString(),
        },
        time_until_sabbath_end: isSabbath ? Math.max(nextSaturday.getTime() - now.getTime(), 0) : null,
        time_until_next_sabbath: !isSabbath ? Math.max(nextFriday.getTime() - now.getTime(), 0) : null,
        user_timezone: tz,
        user_city: settings.company_city || 'Default',
      });
    } catch (err) {
      console.error('Error computing sabbath status:', err);
      setSabbathStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(false);
    setError(null);
  };

  const handleSaveSettings = async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);

    try {
      await setDoc(doc(db, "users", user.uid), {
        sabbathSettings: settings,
      }, { merge: true });

      setSuccess(true);
      if (onSettingsUpdate) {
        onSettingsUpdate(settings);
      }

      if (settings.observe_sabbath && showStatus) {
        computeSabbathStatus();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatSabbathTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch {
      return timeStr;
    }
  };

  const formatTimeUntil = (milliseconds) => {
    if (!milliseconds) return null;

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Custom Date Dialog Component
  const CustomDateDialog = () => (
      <Dialog open={false} onClose={() => {}}>
        <DialogTitle>Add Custom Closure Date</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Closure Date"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                placeholder="e.g., Company Holiday, Personal Day"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                placeholder="Additional details about this closure"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ArgonButton>Cancel</ArgonButton>
          <ArgonButton
            color="info"
            variant="contained"
          >
            Add Date
          </ArgonButton>
        </DialogActions>
      </Dialog>
  );

  if (initialLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
        <ArgonBox mb={3}>
          <ArgonTypography variant="h5" fontWeight="bold" display="flex" alignItems="center">
            <Schedule color="primary" sx={{ mr: 1 }} />
            Sabbath Observance Settings
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" mt={1}>
            Configure automated reporting restrictions for Sabbath observance
          </ArgonTypography>
        </ArgonBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Sabbath settings updated successfully!
          </Alert>
        )}

        {/* Sabbath Status Display */}
        {showStatus && settings.observe_sabbath && (
          <Card sx={{ mb: 3, bgcolor: sabbathStatus?.is_sabbath ? '#fff8f8' : '#f8fff8' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" display="flex" alignItems="center">
                  {sabbathStatus?.is_sabbath ? (
                    <Warning color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  )}
                  Current Sabbath Status
                </ArgonTypography>
                <Tooltip title="Refresh Status">
                  <IconButton onClick={computeSabbathStatus} disabled={statusLoading}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>

              {statusLoading ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography>Loading status...</Typography>
                </Box>
              ) : sabbathStatus ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Status:
                      </Typography>
                      <Chip
                        label={sabbathStatus.is_sabbath ? "Sabbath Observance Active" : "Normal Operations"}
                        color={sabbathStatus.is_sabbath ? "warning" : "success"}
                        icon={sabbathStatus.is_sabbath ? <Warning /> : <CheckCircle />}
                      />
                    </Box>
                  </Grid>

                  {sabbathStatus.sabbath_times && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        This Week&apos;s Sabbath:
                      </Typography>
                      <Typography variant="body2">
                        <strong>Starts:</strong> {formatSabbathTime(sabbathStatus.sabbath_times.sabbath_start)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Ends:</strong> {formatSabbathTime(sabbathStatus.sabbath_times.sabbath_end)}
                      </Typography>
                    </Grid>
                  )}

                  {sabbathStatus.time_until_sabbath_end && (
                    <Grid item xs={12} md={6}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Time until Sabbath ends:</strong><br />
                          {formatTimeUntil(sabbathStatus.time_until_sabbath_end)}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  {sabbathStatus.time_until_next_sabbath && !sabbathStatus.is_sabbath && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Next Sabbath begins in:</strong><br />
                        {formatTimeUntil(sabbathStatus.time_until_next_sabbath)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography color="text.secondary">
                  Unable to load Sabbath status
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Settings Form */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.observe_sabbath}
                  onChange={(e) => handleSettingChange('observe_sabbath', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable Sabbath Observance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Restrict automated reports and notifications from Friday sunset to Saturday nightfall
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {settings.observe_sabbath && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Location Settings
                </ArgonTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Company Timezone"
                  value={settings.company_timezone}
                  onChange={(e) => handleSettingChange('company_timezone', e.target.value)}
                  required
                  helperText="Used for calculating sunset times"
                >
                  {Object.entries(DEFAULT_TIMEZONES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company City"
                  value={settings.company_city}
                  onChange={(e) => handleSettingChange('company_city', e.target.value)}
                  placeholder="e.g., New York, Jerusalem"
                  helperText="For precise sunset calculations"
                  InputProps={{
                    startAdornment: <LocationOn color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Latitude (Optional)"
                  type="number"
                  value={settings.company_latitude || ''}
                  onChange={(e) => handleSettingChange('company_latitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="40.7128"
                  helperText="For most precise calculations"
                  inputProps={{ step: 0.0001 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longitude (Optional)"
                  type="number"
                  value={settings.company_longitude || ''}
                  onChange={(e) => handleSettingChange('company_longitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="-74.0060"
                  helperText="For most precise calculations"
                  inputProps={{ step: 0.0001 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Timing Preferences
                </ArgonTypography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Buffer (minutes before sunset)"
                  type="number"
                  value={settings.sabbath_start_buffer_minutes}
                  onChange={(e) => handleSettingChange('sabbath_start_buffer_minutes', parseInt(e.target.value))}
                  helperText="18 minutes is traditional"
                  inputProps={{ min: 0, max: 120 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Buffer (minutes after sunset)"
                  type="number"
                  value={settings.sabbath_end_buffer_minutes}
                  onChange={(e) => handleSettingChange('sabbath_end_buffer_minutes', parseInt(e.target.value))}
                  helperText="42 minutes is common, 72 minutes for stringent observance"
                  inputProps={{ min: 0, max: 120 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="body2">
                    <strong>How this works:</strong>
                    <br />
                    • Automated reports and notifications will be delayed until after Sabbath ends
                    • Manual reports and user-initiated actions are always allowed
                    • Users can create Sabbath overrides for urgent situations
                    • Sunset times are calculated based on your location for accuracy
                  </Typography>
                </Alert>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <ArgonButton
                color="info"
                variant="contained"
                onClick={handleSaveSettings}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </ArgonButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    {/* Custom Date Dialog */}
    <CustomDateDialog />
    </>
  );
};

SabbathSettings.propTypes = {
  onSettingsUpdate: PropTypes.func,
  showStatus: PropTypes.bool
};

SabbathSettings.defaultProps = {
  onSettingsUpdate: null,
  showStatus: true
};

export default SabbathSettings;
